import React from 'react';
import Icon from 'components/AppIcon';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-secondary-100 text-secondary-700', icon: 'FileEdit' },
  invoiced: { label: 'Invoiced', color: 'bg-primary-50 text-primary', icon: 'FileText' },
  paid: { label: 'Paid', color: 'bg-success-50 text-success-700', icon: 'CheckCircle' }
};

const TimeEntriesList = ({
  entries, loading, filterCase, filterDate, filterStatus,
  onFilterCase, onFilterDate, onFilterStatus,
  onEdit, onDelete, onRefresh
}) => {
  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount) => {
    if (!amount) return '—';
    return `KES ${Number(amount)?.toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            type="text"
            value={filterCase}
            onChange={e => onFilterCase(e?.target?.value)}
            placeholder="Filter by case..."
            className="w-full pl-9 pr-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <input
          type="date"
          value={filterDate}
          onChange={e => onFilterDate(e?.target?.value)}
          className="px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <select
          value={filterStatus}
          onChange={e => onFilterStatus(e?.target?.value)}
          className="px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="invoiced">Invoiced</option>
          <option value="paid">Paid</option>
        </select>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 px-3 py-2.5 border border-border rounded-lg text-sm text-text-secondary hover:bg-secondary-50 transition-colors"
        >
          <Icon name="RefreshCw" size={14} />
          Refresh
        </button>
      </div>
      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-3 text-text-secondary">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            Loading entries...
          </div>
        </div>
      ) : entries?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 bg-secondary-50 rounded-2xl flex items-center justify-center mb-4">
            <Icon name="Clock" size={28} className="text-text-secondary" />
          </div>
          <p className="font-body-medium text-text-primary mb-1">No time entries found</p>
          <p className="text-sm text-text-secondary">Start tracking time or add a manual entry</p>
        </div>
      ) : (
        <div className="bg-surface rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary-50">
                  <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Activity</th>
                  <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Case</th>
                  <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Duration</th>
                  <th className="text-right px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Rate</th>
                  <th className="text-right px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Amount</th>
                  <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Billable</th>
                  <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries?.map(entry => {
                  const statusCfg = STATUS_CONFIG?.[entry?.entry_status] || STATUS_CONFIG?.draft;
                  const canEdit = entry?.entry_status === 'draft';
                  return (
                    <tr key={entry?.id} className="hover:bg-secondary-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                        {formatDate(entry?.started_at || entry?.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-text-primary line-clamp-2 max-w-xs">{entry?.activity_description}</p>
                        {entry?.is_timer_entry && (
                          <span className="text-xs text-primary flex items-center gap-1 mt-0.5">
                            <Icon name="Timer" size={10} /> Timer
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {entry?.case_name ? (
                          <div>
                            <p className="text-sm text-text-primary">{entry?.case_name}</p>
                            {entry?.case_id && <p className="text-xs text-text-secondary">{entry?.case_id}</p>}
                          </div>
                        ) : (
                          <span className="text-sm text-text-secondary">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-body-medium text-text-primary">{formatDuration(entry?.duration_minutes)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-text-secondary">KES {Number(entry?.hourly_rate || 0)?.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {entry?.billable_flag ? (
                          <span className="text-sm font-body-semibold text-success-700">{formatAmount(entry?.billable_amount)}</span>
                        ) : (
                          <span className="text-sm text-text-secondary">Non-billable</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                          entry?.billable_flag ? 'bg-success-50 text-success-600' : 'bg-secondary-100 text-secondary-500'
                        }`}>
                          <Icon name={entry?.billable_flag ? 'Check' : 'Minus'} size={12} />
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-body-medium ${statusCfg?.color}`}>
                          <Icon name={statusCfg?.icon} size={10} />
                          {statusCfg?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => onEdit(entry)}
                            disabled={!canEdit}
                            title={canEdit ? 'Edit entry' : 'Cannot edit invoiced/paid entries'}
                            className={`p-1.5 rounded-lg transition-colors ${
                              canEdit
                                ? 'text-primary hover:bg-primary-50' :'text-secondary-300 cursor-not-allowed'
                            }`}
                          >
                            <Icon name="Edit2" size={14} />
                          </button>
                          <button
                            onClick={() => onDelete(entry?.id)}
                            disabled={!canEdit}
                            title={canEdit ? 'Delete entry' : 'Cannot delete invoiced/paid entries'}
                            className={`p-1.5 rounded-lg transition-colors ${
                              canEdit
                                ? 'text-error-500 hover:bg-error-50' :'text-secondary-300 cursor-not-allowed'
                            }`}
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-border bg-secondary-50 flex items-center justify-between">
            <span className="text-sm text-text-secondary">{entries?.length} {entries?.length === 1 ? 'entry' : 'entries'}</span>
            <span className="text-sm font-body-medium text-text-primary">
              Total billable: KES {entries?.filter(e => e?.billable_flag)?.reduce((s, e) => s + Number(e?.billable_amount || 0), 0)?.toLocaleString('en-KE', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeEntriesList;
